import React, { useEffect, useMemo, useRef, useState } from 'react';

import { DndContext, type DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSnackbar } from 'notistack';

import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material';

import { isAccommodationPackage } from '~/components/Offers/Detail/typeNarrowingUtils';

import type { PackageInclusionsByPackageId } from '~/hooks/apis/inclusions/useGetPackageInclusionsByOffer';
import { useSaveOfferPackage } from '~/hooks/apis/offers/useSaveOfferPackage';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import type { PropertyRoomTypeWithImages } from '../ImagesVideo/types';
import { type MasterInclusion, buildPackageInclusionsFromMaster, getPackageOptions } from '../Inclusions/utils';
import { type PackageInclusionRecord, isInclusionHidden } from '../utils';

import SortableOfferPackageRow from './SortableOfferPackageRow';
import { buildAutofillPackageValues } from './autofillPackage';
import { DEFAULT_PACKAGE_TAB } from './types';
import type { OfferBuilderPackage, PackageTab } from './types';
import {
  buildOfferBuilderPackages,
  buildPackageSavePayload,
  getPackageInclusions,
  getPackageName,
  getPackagePersistId,
  getPackageStatus,
} from './utils';

interface Props {
  isLoadingOffer?: boolean;
  masterInclusions?: Array<MasterInclusion>;
  offerId?: string;
  offerError?: unknown;
  offerType?: App.OfferType;
  packageInclusions?: PackageInclusionsByPackageId;
  packages?: Array<App.Package>;
  property?: App.Property;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  vendorId?: string;
}

const EMPTY_PACKAGES: Array<App.Package> = [];

function getPackageKey(offerPackages: Array<App.Package>) {
  return JSON.stringify({
    packages: offerPackages.map((offerPackage) => ({
      id: offerPackage.id_salesforce_external ?? offerPackage.le_package_id,
      name: offerPackage.deal_option_name ?? offerPackage.name,
      sortOrder: (offerPackage as App.Package & { sort_order?: unknown }).sort_order,
      status: offerPackage.status,
    })),
  });
}

function buildPackageInclusionsByPackageId(
  offerPackages: Array<App.Package>,
  masterInclusions?: Array<MasterInclusion>,
) {
  const packageOptions = getPackageOptions(offerPackages);

  return masterInclusions ? buildPackageInclusionsFromMaster(masterInclusions, packageOptions) : undefined;
}

function getInclusionPersistId(inclusion: PackageInclusionRecord) {
  const inclusionId = inclusion.inclusion_id ?? inclusion.inclusion?.id;

  return inclusionId ? String(inclusionId) : undefined;
}

function getInclusionLookupKey(inclusion: PackageInclusionRecord) {
  const persistId = getInclusionPersistId(inclusion);

  if (persistId) {
    return `id:${persistId}`;
  }

  const name = inclusion.inclusion?.name ?? inclusion.name ?? inclusion.content;

  return name ? `name:${name.trim().toLowerCase()}` : undefined;
}

function buildPackageInclusionLookup(inclusions?: Array<PackageInclusionRecord>) {
  return (inclusions ?? []).reduce<Map<string, PackageInclusionRecord>>((inclusionsByKey, inclusion) => {
    const key = getInclusionLookupKey(inclusion);

    if (key && !inclusionsByKey.has(key)) {
      inclusionsByKey.set(key, inclusion);
    }

    return inclusionsByKey;
  }, new Map());
}

function preservePackageInclusionFields(
  masterInclusion: PackageInclusionRecord,
  packageInclusion?: PackageInclusionRecord,
): PackageInclusionRecord {
  if (!packageInclusion) {
    return masterInclusion;
  }

  return {
    ...packageInclusion,
    ...masterInclusion,
    id: packageInclusion.id ?? masterInclusion.id,
    inclusion_id: packageInclusion.inclusion_id ?? masterInclusion.inclusion_id,
    inclusion: {
      ...packageInclusion.inclusion,
      ...masterInclusion.inclusion,
      id: masterInclusion.inclusion?.id ?? packageInclusion.inclusion?.id,
    },
    sort_order: packageInclusion.sort_order,
    style: 'style' in packageInclusion ? packageInclusion.style : masterInclusion.style,
    lux_plus_tier: 'lux_plus_tier' in packageInclusion ? packageInclusion.lux_plus_tier : masterInclusion.lux_plus_tier,
  };
}

function applyPackageInclusions(
  masterInclusions: Array<PackageInclusionRecord>,
  salesforcePackageInclusions?: Array<PackageInclusionRecord>,
) {
  const packageInclusionsByKey = buildPackageInclusionLookup(salesforcePackageInclusions);
  const appliedMasterInclusions = masterInclusions
    .map((masterInclusion) => {
      const key = getInclusionLookupKey(masterInclusion);

      return preservePackageInclusionFields(masterInclusion, key ? packageInclusionsByKey.get(key) : undefined);
    })
    .sort(
      (firstInclusion, secondInclusion) =>
        (firstInclusion.sort_order ?? Number.POSITIVE_INFINITY) -
        (secondInclusion.sort_order ?? Number.POSITIVE_INFINITY),
    );
  const hiddenInclusions = salesforcePackageInclusions ? salesforcePackageInclusions.filter(isInclusionHidden) : [];

  return appliedMasterInclusions.concat(hiddenInclusions);
}

export function mergePackageInclusions(
  offerPackages: Array<App.Package>,
  packageInclusionsByPackageId: Record<string, Array<PackageInclusionRecord>> | undefined,
  packageInclusions?: PackageInclusionsByPackageId,
) {
  const packageOptions = getPackageOptions(offerPackages);

  return offerPackages.map((offerPackage, index) => {
    const packageId = packageOptions[index]?.id ?? `package-${index}`;
    const salesforcePackageInclusions = offerPackage.id_salesforce_external
      ? packageInclusions?.[offerPackage.id_salesforce_external]
      : undefined;
    const masterInclusions = packageInclusionsByPackageId?.[packageId];
    const inclusions = masterInclusions
      ? applyPackageInclusions(masterInclusions, salesforcePackageInclusions)
      : salesforcePackageInclusions;

    if (!inclusions) {
      return offerPackage;
    }

    return {
      ...offerPackage,
      inclusions,
    };
  });
}

function getAvailabilityRatesUrl(offerPackage: App.Package, vendorId?: string) {
  if (
    !vendorId ||
    !isAccommodationPackage(offerPackage) ||
    !offerPackage.fk_property_id ||
    !offerPackage.fk_room_type_id ||
    !offerPackage.fk_room_rate_id
  ) {
    return undefined;
  }

  return `/vendors/${vendorId}/properties/${offerPackage.fk_property_id}/room-types/${offerPackage.fk_room_type_id}/room-rates/${offerPackage.fk_room_rate_id}`;
}

export default function OfferPackages({
  isLoadingOffer,
  masterInclusions,
  offerId,
  offerError,
  offerType,
  packageInclusions,
  packages,
  property,
  propertyRoomTypes,
  vendorId,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const offerPackages = packages ?? EMPTY_PACKAGES;
  const packageInclusionsByPackageId = useMemo(
    () => buildPackageInclusionsByPackageId(offerPackages, masterInclusions),
    [masterInclusions, offerPackages],
  );
  const initialPackages = useMemo(() => {
    return buildOfferBuilderPackages(
      mergePackageInclusions(offerPackages, packageInclusionsByPackageId, packageInclusions),
    );
  }, [packageInclusions, packageInclusionsByPackageId, offerPackages]);
  const [orderedPackages, setOrderedPackages] = useState<Array<OfferBuilderPackage>>([]);
  const [expandedPackageIds, setExpandedPackageIds] = useState<Array<string>>([]);
  const [activePackageTabs, setActivePackageTabs] = useState<Record<string, PackageTab>>({});
  const [savingPackageId, setSavingPackageId] = useState<string>();
  const [saveAllProgress, setSaveAllProgress] = useState<{ completed: number; total: number }>();
  const saveOfferPackage = useSaveOfferPackage();
  const packageStateKey = useMemo(() => getPackageKey(offerPackages), [offerPackages]);
  const previousPackageStateKey = useRef<string | undefined>(undefined);
  const isSavingAllPackagesRef = useRef(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const packageSortableIds = useMemo(
    () => orderedPackages.map((offerPackage) => offerPackage.sortableId),
    [orderedPackages],
  );
  const rowPackages = useMemo(() => orderedPackages.map((orderedPackage) => orderedPackage.package), [orderedPackages]);
  const isSavingPackages = !!savingPackageId || !!saveAllProgress;

  useEffect(() => {
    if (previousPackageStateKey.current === packageStateKey) {
      return;
    }

    previousPackageStateKey.current = packageStateKey;
    setOrderedPackages(initialPackages);
    setExpandedPackageIds([]);
    setActivePackageTabs({});
  }, [initialPackages, packageStateKey]);

  useEffect(() => {
    if (!packageInclusionsByPackageId) {
      return;
    }

    setOrderedPackages((currentPackages) =>
      currentPackages.map((offerPackage) => {
        const packageId = getPackagePersistId(offerPackage.package);
        const masterInclusions = packageId ? packageInclusionsByPackageId[packageId] : undefined;

        if (!masterInclusions) {
          return offerPackage;
        }

        const salesforcePackageInclusions = packageId ? packageInclusions?.[packageId] : undefined;
        const currentPackageInclusions = salesforcePackageInclusions ?? getPackageInclusions(offerPackage.package);

        return {
          ...offerPackage,
          package: {
            ...offerPackage.package,
            inclusions: applyPackageInclusions(masterInclusions, currentPackageInclusions),
          },
        };
      }),
    );
  }, [packageInclusions, packageInclusionsByPackageId]);

  const areAllPackagesExpanded =
    orderedPackages.length > 0 &&
    orderedPackages.every((offerPackage) => expandedPackageIds.includes(offerPackage.sortableId));

  const handleToggleAllPackages = () => {
    setExpandedPackageIds(areAllPackagesExpanded ? [] : orderedPackages.map((offerPackage) => offerPackage.sortableId));
  };

  const handleExpandedChange = (packageId: string, isExpanded: boolean) => {
    if (isExpanded) {
      setActivePackageTabs((currentTabs) => ({
        ...currentTabs,
        [packageId]: currentTabs[packageId] ?? DEFAULT_PACKAGE_TAB,
      }));
    }

    setExpandedPackageIds((currentPackageIds) =>
      isExpanded
        ? Array.from(new Set(currentPackageIds.concat(packageId)))
        : currentPackageIds.filter((currentPackageId) => currentPackageId !== packageId),
    );
  };

  const handlePackageChange = (
    packageId: string,
    nextPackageValues: Partial<App.Package> & Record<string, unknown>,
  ) => {
    setOrderedPackages((currentPackages) =>
      currentPackages.map((offerPackage) => {
        if (offerPackage.sortableId !== packageId) {
          return offerPackage;
        }

        const nextPackage = { ...offerPackage.package, ...nextPackageValues };
        const status = getPackageStatus(nextPackage);

        return {
          ...offerPackage,
          name: getPackageName(nextPackage),
          package: nextPackage,
          // Keep the header summary stable while editing fields that affect its text.
          packageSummary: offerPackage.packageSummary,
          statusColor: status.color,
          statusLabel: status.label,
        };
      }),
    );
  };

  const handleAutofill = (packageId: string) => {
    const targetPackage = orderedPackages.find((offerPackage) => offerPackage.sortableId === packageId)?.package;

    if (!targetPackage) {
      return;
    }

    const autofillValues = buildAutofillPackageValues({
      offerPackage: targetPackage,
      property,
      offerType,
    });

    if (autofillValues) {
      handlePackageChange(packageId, autofillValues);
      enqueueSnackbar(`Package autofilled from ${autofillValues.name}`, { variant: 'success' });
      return;
    }

    enqueueSnackbar('No matching flash room type and rate found for this deal option', { variant: 'warning' });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setOrderedPackages((currentPackages) => {
      const oldIndex = currentPackages.findIndex((offerPackage) => offerPackage.sortableId === active.id);
      const newIndex = currentPackages.findIndex((offerPackage) => offerPackage.sortableId === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return currentPackages;
      }

      return arrayMove(currentPackages, oldIndex, newIndex);
    });
  };

  const savePackage = async (offerPackage: OfferBuilderPackage, sortOrder?: number) => {
    const payload = buildPackageSavePayload({
      offerId,
      offerPackage: offerPackage.package,
      sortOrder,
      vendorId,
    });

    if (!payload.offerId || !payload.packageId) {
      throw new Error('Unable to save package without an offer ID and package ID.');
    }

    await saveOfferPackage.mutateAsync({
      offerId: payload.offerId,
      packageData: payload.packageData,
      packageId: payload.packageId,
      packageInclusions: payload.packageInclusions,
    });
  };

  const handleSavePackage = async (offerPackage: OfferBuilderPackage) => {
    setSavingPackageId(offerPackage.sortableId);

    try {
      await savePackage(offerPackage);
      enqueueSnackbar(`${offerPackage.name} saved`, { variant: 'success' });
    } catch (error) {
      reportError(error);
      enqueueSnackbar(getErrorMessage(error, `Failed to save ${offerPackage.name}`), { variant: 'error' });
    } finally {
      setSavingPackageId(undefined);
    }
  };

  const handleSaveAllPackages = async () => {
    if (isSavingAllPackagesRef.current) {
      return;
    }

    isSavingAllPackagesRef.current = true;
    setSaveAllProgress({ completed: 0, total: orderedPackages.length });
    let completedPackageCount = 0;
    let failedPackageName: string | undefined;

    try {
      for (const [index, offerPackage] of orderedPackages.entries()) {
        failedPackageName = offerPackage.name;
        setSavingPackageId(offerPackage.sortableId);
        await savePackage(offerPackage, index + 1);
        completedPackageCount = index + 1;
        setSaveAllProgress({ completed: index + 1, total: orderedPackages.length });
      }

      enqueueSnackbar('All packages saved', { variant: 'success' });
    } catch (error) {
      reportError(error);
      const failureSummary = failedPackageName
        ? `Failed to save ${failedPackageName} (${completedPackageCount}/${orderedPackages.length} completed)`
        : 'Failed to save all packages';

      enqueueSnackbar(`${failureSummary}: ${getErrorMessage(error)}`, { variant: 'error' });
    } finally {
      isSavingAllPackagesRef.current = false;
      setSavingPackageId(undefined);
      setSaveAllProgress(undefined);
    }
  };

  if (offerError) {
    return <Alert severity="error">Unable to load offer packages.</Alert>;
  }

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">Offer packages</Typography>
          <Typography variant="body1" color="text.secondary">
            Order packages from base to most expensive. Verify each package is listed on the hotel's website.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            onClick={handleToggleAllPackages}
            disabled={orderedPackages.length === 0}
          >
            {areAllPackagesExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
        >
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ fontWeight: 700 }}>
              {orderedPackages.length}
            </Box>{' '}
            {orderedPackages.length === 1 ? 'package' : 'packages'} · drag{' '}
            <Box component="span" sx={{ color: 'text.disabled' }}>
              ::
            </Box>{' '}
            to reorder
          </Typography>
          <Button
            variant="contained"
            size="small"
            disabled={orderedPackages.length === 0 || isSavingPackages}
            onClick={() => void handleSaveAllPackages()}
          >
            {saveAllProgress
              ? `Saving ${Math.min(saveAllProgress.completed + 1, saveAllProgress.total)}/${saveAllProgress.total}`
              : 'Save all'}
          </Button>
        </Stack>
      </Paper>

      {orderedPackages.length === 0 && !isLoadingOffer && (
        <Alert severity="info">No packages are currently available for this offer.</Alert>
      )}

      <Box sx={{ bgcolor: 'grey.50', border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={packageSortableIds} strategy={verticalListSortingStrategy}>
            <Stack spacing={2}>
              {orderedPackages.map((offerPackage) => {
                const activePackageTab = activePackageTabs[offerPackage.sortableId] ?? DEFAULT_PACKAGE_TAB;

                return (
                  <SortableOfferPackageRow
                    key={offerPackage.sortableId}
                    activePackageTab={activePackageTab}
                    expanded={expandedPackageIds.includes(offerPackage.sortableId)}
                    isSaveDisabled={isSavingPackages}
                    isSaving={savingPackageId === offerPackage.sortableId}
                    offerId={offerId}
                    offerPackage={offerPackage}
                    availabilityRatesUrl={getAvailabilityRatesUrl(offerPackage.package, vendorId)}
                    onAutofill={() => handleAutofill(offerPackage.sortableId)}
                    packages={activePackageTab === 'Package inclusions' ? rowPackages : undefined}
                    propertyRoomTypes={propertyRoomTypes}
                    vendorId={vendorId}
                    onChange={(nextPackageValues) => handlePackageChange(offerPackage.sortableId, nextPackageValues)}
                    onExpandedChange={(isExpanded) => handleExpandedChange(offerPackage.sortableId, isExpanded)}
                    onSave={() => void handleSavePackage(offerPackage)}
                    onTabChange={(tab) =>
                      setActivePackageTabs((currentTabs) => ({ ...currentTabs, [offerPackage.sortableId]: tab }))
                    }
                  />
                );
              })}
            </Stack>
          </SortableContext>
        </DndContext>
      </Box>
    </Box>
  );
}
